import React, { FC, useState, useRef, useEffect } from 'react';
import { useNavigate, useParams, useOutletContext } from 'react-router-dom';
import { Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

import { ReactComponent as ProgramWorkoutIcon } from 'src/assets/images/trainer/clients/program_workout.svg';
import { ReactComponent as NavArrowIcon } from 'src/assets/images/trainer/clients/nav-arrow-icon.svg';
import { ReactComponent as MoreProgramWorkoutIcon } from 'src/assets/images/trainer/clients/more_program_workout.svg';
import { ReactComponent as UpNextIcon } from 'src/assets/images/onboarding-trainer/dashboard/programs/up_next.svg';
import { ReactComponent as WorkoutHistoryIcon } from 'src/assets/images/onboarding-trainer/dashboard/programs/workout-history.svg';

import { useGetClientWorkoutsTab } from 'src/api/queries/useGetClientWorkoutsTab';
import { isMaleProfile } from 'src/utils/genderUtils';
import { sessionStore } from 'src/store/sessionStore';
import { EmptyState } from './EmptyState';

import './WorkoutsTab.scss';

interface WorkoutsTabProps {
  client?: any;
}

export const WorkoutsTab: FC<WorkoutsTabProps> = () => {
  const { client } = useOutletContext<{ client: any }>();
  const [visibleThumbnails, setVisibleThumbnails] = useState(7);
  const thumbnailsContainerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { uid } = useParams();
  const { currentUser } = sessionStore();
  const trainerId = currentUser?.uid;

  const { data, isLoading } = useGetClientWorkoutsTab(uid || '', trainerId);

  useEffect(() => {
    const updateVisibleCount = () => {
      if (thumbnailsContainerRef.current) {
        // Get container width and calculate how many 80px thumbnails + 12px gaps (92px) can fit.
        // We subtract 1 to reserve space for the .more-btn which is always displayed.
        const containerWidth = thumbnailsContainerRef.current.clientWidth;
        const maxItems = Math.max(1, Math.floor(containerWidth / 92) - 1);
        setVisibleThumbnails(maxItems);
      }
    };

    updateVisibleCount();
    window.addEventListener('resize', updateVisibleCount);
    return () => window.removeEventListener('resize', updateVisibleCount);
  }, []);

  const formatDate = (isoStr: string | null | undefined) => {
    if (!isoStr) return '-';
    if (isoStr.includes('•') && !isoStr.includes('T')) return isoStr;
    try {
      const parsed = dayjs(isoStr);
      if (parsed.isValid()) {
        const currentYear = dayjs().year();
        const finalDate =
          parsed.year() === 2001 && !isoStr.includes('2001') ? parsed.year(currentYear) : parsed;
        return finalDate.format('MMM DD, YYYY • h:mm A');
      }
      return isoStr;
    } catch {
      return isoStr;
    }
  };

  if (isLoading) {
    return (
      <div
        className="workouts-tab-container loading"
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 400,
          gap: 16,
        }}
      >
        <Spin indicator={<LoadingOutlined style={{ fontSize: 32, color: '#ffa100' }} spin />} />
        <span style={{ fontSize: 16, fontWeight: 500, color: '#ffffffb3' }}>Loading Workouts</span>
      </div>
    );
  }

  const workouts = data?.workouts || [];
  const isDraftProgram = data?.isDraft === true || (data as any)?.status === 'draft';
  const generatorType =
    (data as any)?.generatorType === 'manual' || (data as any)?.type === 'manual' ? 'manual' : 'ai';

  if (workouts.length === 0 || isDraftProgram) {
    return (
      <div className="workouts-tab-container">
        <div
          className="dashboard-empty-state-wrapper"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '400px',
            width: '100%',
          }}
        >
          <EmptyState
            message={
              isDraftProgram
                ? 'You have an unfinished program draft for this client.'
                : 'No Workouts Recorded Yet'
            }
            buttonText={isDraftProgram ? 'Continue to Finish Program' : undefined}
            onButtonClick={
              isDraftProgram
                ? () =>
                    navigate(
                      `/onboarding-trainer/dashboard/program-generator/${generatorType}?programId=${(data as any)?.programId}`,
                    )
                : undefined
            }
            hideIcon
            hideMessage={false}
            marginTop="0"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="workouts-tab-container">
      <div className="workouts-tab-content">
        <div className="left-column">
          {workouts.map((workout, idx) => {
            const isFirst = idx === 0;
            return (
              <React.Fragment key={idx}>
                {workout.isUpNext && (
                  <div className="up-next-section">
                    <div className="up-next-label">
                      <UpNextIcon />
                      <span>Up next</span>
                    </div>
                  </div>
                )}
                <div className="program-workout-card">
                  <div className="card-header">
                    <div className="title-group">
                      <ProgramWorkoutIcon className="workout-icon" />
                      <h3>
                        <span className="main-title">{workout.title} </span>
                        <span className="subtitle">&bull; {workout.dateLabel}</span>
                      </h3>
                    </div>
                    <NavArrowIcon
                      className="nav-arrow cursor-pointer"
                      onClick={() =>
                        navigate(`/onboarding-trainer/dashboard/clients/${uid}/workouts/${idx + 1}`)
                      }
                    />
                  </div>
                  <div className="thumbnails-row" ref={isFirst ? thumbnailsContainerRef : null}>
                    {workout.exercises
                      .slice(0, visibleThumbnails)
                      .map((item: any, eIdx: number) => {
                        const isMale = isMaleProfile(data || client);

                        const imgUrl = isMale
                          ? item.image_male_url || item.image_female_url
                          : item.image_female_url || item.image_male_url;

                        return (
                          <div key={eIdx} className="thumbnail">
                            {imgUrl ? (
                              <img
                                src={imgUrl}
                                alt={item.en_display_name || item.name || 'Exercise'}
                              />
                            ) : (
                              <div
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  backgroundColor: '#2a2d3d',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  borderRadius: '8px',
                                  color: '#8a8d9d',
                                  fontSize: '12px',
                                  fontWeight: 'bold',
                                  textTransform: 'uppercase',
                                }}
                              >
                                {(item.en_display_name || item.name || 'EX').substring(0, 2)}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    {workout.exercises.length > 0 && (
                      <div
                        className="thumbnail more-btn cursor-pointer"
                        onClick={() =>
                          navigate(
                            `/onboarding-trainer/dashboard/clients/${uid}/workouts/${idx + 1}`,
                          )
                        }
                      >
                        <MoreProgramWorkoutIcon />
                      </div>
                    )}
                  </div>
                </div>
              </React.Fragment>
            );
          })}
          {workouts.length === 0 && (
            <div style={{ color: '#fff', opacity: 0.7, padding: '24px' }}>
              No workouts scheduled.
            </div>
          )}
        </div>

        <div className="right-column">
          <div className="workout-history-card">
            <div className="card-header">
              <div className="title-group">
                <WorkoutHistoryIcon className="history-icon" />
                <h3>Workout History</h3>
              </div>
            </div>
            <div className="history-list">
              <div className="history-item">
                <span className="label">Last workout completed</span>
                <span className="value">{formatDate(data?.lastWorkoutCompleted)}</span>
              </div>
              <div className="history-item">
                <span className="label">Next scheduled workout</span>
                <span className="value">{formatDate(data?.nextScheduledWorkout)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
